import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.tools')

export const getToolById = async (supabase: SupabaseClient<Database>, toolId: string) => {
  const { data: tool, error } = await supabase
    .from("tools")
    .select("*")
    .eq("id", toolId)
    .single()

  if (!tool) {
    logError(logger, error, 'Failed to fetch Tool by ID', { toolId })
    throw new Error(error.message)
  }

  return tool
}

export const getToolsByIds = async (
  supabase: SupabaseClient<Database>,
  toolIds: string[]
) => {
  const { data: tools, error } = await supabase
    .from("tools")
    .select("*")
    .in("id", toolIds)

  if (error) {
    logError(logger, error, 'Failed to fetch Tools by IDs', { toolIds })
    throw new Error(error.message)
  }

  return tools
}

export const getOptionalToolById = async (supabase: SupabaseClient<Database>, toolId: string) => {
  const { data: tool, error } = await supabase
    .from("tools")
    .select("*")
    .eq("id", toolId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Tool by ID', { toolId })
    throw new Error(error.message)
  }

  return tool
}

export const getOptionalSystemToolByName = async (supabase: SupabaseClient<Database>, toolName: string) => {
  const { data: tool, error } = await supabase
    .from("tools")
    .select("*")
    .eq("name", toolName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Tool by ID', { toolName })
    throw new Error(error.message)
  }

  return tool
}

export const getOptionalToolByName = async (supabase: SupabaseClient<Database>, toolName: string,
		workspaceId: string) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
      `
      tools (*)
    `
    )
    .eq("id", workspaceId)
    .eq("tools.name", toolName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Tool by name', { toolName, workspaceId })
    throw new Error(error.message)
  }

  return data ? data.tools[0] : null
}

export const getToolWorkspacesByWorkspaceId = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      tools (*)
    `
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
    logError(logger, error, 'Failed to fetch Tool workspaces by workspace ID', { workspaceId })
    throw new Error(error.message)
  }

  return workspace.tools ?? []
}

export const getToolsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'tools'>[]> => {
	const wsTools = await getToolWorkspacesByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'tools'>[] = []

	for (const [i, tool] of wsTools.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "tools", context: "ANY", uuid: tool.id, tenant:tool.owning_tenant, ws: workspace})

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "tools", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...tool, permissions: perms })
	}

	return result
}

export const getToolWorkspacesByToolId = async (supabase: SupabaseClient<Database>, toolId: string) => {
  const { data: tool, error } = await supabase
    .from("tools")
    .select(
      `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", toolId)
    .single()

  if (!tool) {
    logError(logger, error, 'Failed to fetch Tool workspaces by Tool ID', { toolId })
    throw new Error(error.message)
  }

  return tool
}

export const getAllTools = async (supabase: SupabaseClient<Database>) => {
  const { data: tools, error } = await supabase
    .from("tools")
    .select("*")

  if (!tools) {
    logError(logger, error, 'Failed to fetch all Tools')
    throw new Error(error.message)
  }

  return tools
}

export const getAutoShareTools = async (supabase: SupabaseClient<Database>) => {
  const { data: tools, error } = await supabase
    .from("tools")
    .select("*")
    .eq("auto_share", true)

  if (error) {
    logError(logger, error, 'Failed to fetch auto-share Tools')
    throw new Error(error.message)
  }

  return tools
}

export const createTool = async (
	supabase: SupabaseClient<Database>,
  tool: TablesInsert<"tools">,
  workspace: Tables<"workspaces">
) => {
	const toolWithTenant = {
		...tool,
		owning_tenant: workspace.tenant_id
	}

  const { data: createdTool, error } = await supabase
    .from("tools")
    .insert([toolWithTenant])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Tool', { tool: printObject(tool) })
    throw new Error(error.message)
  }

  await createToolWorkspace(supabase, {
    user_id: createdTool.user_id,
    tool_id: createdTool.id,
    workspace_id: workspace.id
  })

  return createdTool
}

export const createTools = async (
	supabase: SupabaseClient<Database>,
  tools: TablesInsert<"tools">[],
  workspace: Tables<"workspaces">
) => {
	const toolsWithTenant = tools.map(tool => ({
		...tool,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdTools, error } = await supabase
    .from("tools")
    .insert(toolsWithTenant)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Tools', { tools: printObject(tools) })
    throw new Error(error.message)
  }

  await createToolWorkspaces(supabase,
    createdTools.map(tool => ({
      user_id: tool.user_id,
      tool_id: tool.id,
	    workspace_id: workspace.id
    }))
  )

  logger.info('Tools created successfully', { toolIds: createdTools.map(tool => tool.id) })
  return createdTools
}

export const createToolWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  tool_id: string
  workspace_id: string
}) => {
  const { data: createdToolWorkspace, error } = await supabase
    .from("tool_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Tool workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

  logger.info('Tool workspace created successfully', { createdToolWorkspace })
  return createdToolWorkspace
}

export const createToolWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; tool_id: string; workspace_id: string }[]
) => {
  const { data: createdToolWorkspaces, error } = await supabase
    .from("tool_workspaces")
    .upsert(items, { onConflict: 'tool_id, workspace_id' })
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Tool workspaces', { items: printObject(items) })
    throw new Error(error.message)
	}
  logger.info('Tool workspaces created successfully', { createdToolWorkspaces })
  return createdToolWorkspaces
}

export const updateTool = async (
	supabase: SupabaseClient<Database>,
  toolId: string,
  tool: TablesUpdate<"tools">
) => {
  const { data: updatedTool, error } = await supabase
    .from("tools")
    .update(tool)
    .eq("id", toolId)
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to update Tool', { toolId, tool: printObject(tool) })
    throw new Error(error.message)
  }

  return updatedTool
}

export const deleteTool = async (supabase: SupabaseClient<Database>, toolId: string) => {
  const { error } = await supabase.from("tools").delete().eq("id", toolId)

  if (error) {
    logError(logger, error, 'Failed to delete Tool', { toolId })
    throw new Error(error.message)
  }

  return true
}

export const deleteToolWorkspace = async (
	supabase: SupabaseClient<Database>,
  toolId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("tool_workspaces")
    .delete()
    .eq("tool_id", toolId)
    .eq("workspace_id", workspaceId)

  if (error) {
    logError(logger, error, 'Failed to delete Tool workspace', { toolId, workspaceId })
    throw new Error(error.message)
	}
  return true
}
